import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// 🚀 최종 교정: 대표님이 찾으신 완벽한 6MB짜리 한글 폰트 파일명으로 정확히 매핑
Font.register({
  family: 'NotoSansKR',
  fonts: [
    { src: '/fonts/NotoSansKR-Regular.ttf', fontWeight: 'normal' }, 
    { src: '/fonts/NotoSansKR-Bold.ttf', fontWeight: 'bold' } 
  ]
});

const styles = StyleSheet.create({
  page: { padding: 30, fontFamily: 'NotoSansKR', fontSize: 9, color: '#000000' },
  mainTitle: { fontSize: 24, width: '100%', textAlign: 'center', marginBottom: 20, fontWeight: 'bold' },
  
  infoWrap: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 },
  infoBox: { width: '48%', borderTopWidth: 1, borderLeftWidth: 1, borderColor: '#000000' },
  row: { flexDirection: 'row' },
  
  cellHeader: { width: '35%', backgroundColor: '#f8fafc', paddingVertical: 6, paddingHorizontal: 2, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000000' },
  cellData: { width: '65%', paddingVertical: 6, paddingHorizontal: 4, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000000' },

  table: { borderTopWidth: 1, borderLeftWidth: 1, borderColor: '#000000', marginTop: 10 },
  th: { paddingVertical: 8, paddingHorizontal: 2, backgroundColor: '#f8fafc', borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000000' },
  td: { paddingVertical: 8, paddingHorizontal: 4, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000000' },

  textCenter: { width: '100%', textAlign: 'center' },
  textCenterBold: { width: '100%', textAlign: 'center', fontWeight: 'bold' },
  textLeft: { width: '100%', textAlign: 'left' },
  textRight: { width: '100%', textAlign: 'right' },
  textRightBold: { width: '100%', textAlign: 'right', fontWeight: 'bold' },

  footer: { marginTop: 30, alignItems: 'center' },
  signBox: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 20 }
});

const PartSalesInvoicePdf = ({ invoiceData }) => {
  if (!invoiceData) return null;

  const {
    customerName = '', phone = '', address = '', completeDate = '',
    provider = { 
      name: '주식회사 이투빌리티(이주희,김종배)', bizNum: '694-85-02998', 
      regNum: '01-4118-000368', address: '경기도 하남시 동남로 385' 
    },
    items = []
  } = invoiceData;

  const vatRate = 0.1;
  let subTotal = 0;
  
  const computedItems = items.map(item => {
    const qty = Number(item.qty || 0);
    const price = Number(item.price || 0);
    const labor = Number(item.labor || 0);
    const sum = (price * qty) + labor;
    subTotal += sum;
    return { ...item, qty, price, labor, calculatedSum: sum };
  });

  const vat = Math.round(subTotal * vatRate);
  const total = subTotal + vat;
  const blankRows = Array.from({ length: Math.max(0, 10 - computedItems.length) });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.mainTitle}>거래명세서</Text>

        <View style={styles.infoWrap}>
          <View style={styles.infoBox}>
            <View style={styles.row}>
              <View style={[styles.cellHeader, { width: '100%' }]}><Text style={styles.textCenterBold}>공 급 받 는 자</Text></View>
            </View>
            <View style={styles.row}>
              <View style={styles.cellHeader}><Text style={styles.textCenterBold}>상호(성명)</Text></View>
              <View style={styles.cellData}><Text style={styles.textCenterBold}>{customerName}</Text></View>
            </View>
            <View style={styles.row}>
              <View style={styles.cellHeader}><Text style={styles.textCenterBold}>연 락 처</Text></View>
              <View style={styles.cellData}><Text style={styles.textCenter}>{phone || '-'}</Text></View>
            </View>
            <View style={styles.row}>
              <View style={styles.cellHeader}><Text style={styles.textCenterBold}>주 소</Text></View>
              <View style={styles.cellData}><Text style={styles.textCenter}>{address || '-'}</Text></View>
            </View>
          </View>

          <View style={styles.infoBox}>
            <View style={styles.row}>
              <View style={[styles.cellHeader, { width: '100%' }]}><Text style={styles.textCenterBold}>공 급 자</Text></View>
            </View>
            <View style={styles.row}>
              <View style={styles.cellHeader}><Text style={styles.textCenterBold}>사업자번호</Text></View>
              <View style={styles.cellData}><Text style={styles.textCenter}>{provider.bizNum}</Text></View>
            </View>
            <View style={styles.row}>
              <View style={styles.cellHeader}><Text style={styles.textCenterBold}>상호(대표)</Text></View>
              <View style={styles.cellData}><Text style={styles.textCenter}>{provider.name}</Text></View>
            </View>
            <View style={styles.row}>
              <View style={styles.cellHeader}><Text style={styles.textCenterBold}>주 소</Text></View>
              <View style={styles.cellData}><Text style={styles.textCenter}>{provider.address}</Text></View>
            </View>
          </View>
        </View>

        <View style={{ marginBottom: 5 }}>
          <Text style={{ width: '100%', fontWeight: 'bold' }}>거래일자: {completeDate}</Text>
        </View>

        <View style={styles.table}>
          <View style={{ flexDirection: 'row' }}>
            <View style={[styles.th, { width: '6%' }]}><Text style={styles.textCenterBold}>No</Text></View>
            <View style={[styles.th, { width: '34%' }]}><Text style={styles.textCenterBold}>품목 (부품명)</Text></View>
            <View style={[styles.th, { width: '8%' }]}><Text style={styles.textCenterBold}>수량</Text></View>
            <View style={[styles.th, { width: '13%' }]}><Text style={styles.textCenterBold}>단가</Text></View>
            <View style={[styles.th, { width: '13%' }]}><Text style={styles.textCenterBold}>부품가</Text></View>
            <View style={[styles.th, { width: '13%' }]}><Text style={styles.textCenterBold}>기술료</Text></View>
            <View style={[styles.th, { width: '13%' }]}><Text style={styles.textCenterBold}>소계</Text></View>
          </View>

          {computedItems.map((item, idx) => (
            <View key={idx} style={{ flexDirection: 'row' }}>
              <View style={[styles.td, { width: '6%' }]}><Text style={styles.textCenter}>{idx + 1}</Text></View>
              <View style={[styles.td, { width: '34%' }]}><Text style={styles.textLeft}>{item.taskName}</Text></View>
              <View style={[styles.td, { width: '8%' }]}><Text style={styles.textCenter}>{item.qty}</Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text style={styles.textRight}>{item.price.toLocaleString()}</Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text style={styles.textRight}>{(item.price * item.qty).toLocaleString()}</Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text style={styles.textRight}>{item.labor.toLocaleString()}</Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text style={styles.textRight}>{item.calculatedSum.toLocaleString()}</Text></View>
            </View>
          ))}

          {blankRows.map((_, idx) => (
            <View key={`blank-${idx}`} style={{ flexDirection: 'row' }}>
              <View style={[styles.td, { width: '6%' }]}><Text> </Text></View>
              <View style={[styles.td, { width: '34%' }]}><Text> </Text></View>
              <View style={[styles.td, { width: '8%' }]}><Text> </Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text> </Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text> </Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text> </Text></View>
              <View style={[styles.td, { width: '13%' }]}><Text> </Text></View>
            </View>
          ))}

          <View style={{ flexDirection: 'row', backgroundColor: '#f1f5f9' }}>
            <View style={[styles.td, { width: '50%' }]}><Text style={styles.textCenterBold}>합 계</Text></View>
            <View style={[styles.td, { width: '25%' }]}><Text style={styles.textRightBold}>공급가액: {subTotal.toLocaleString()} 원</Text></View>
            <View style={[styles.td, { width: '25%' }]}><Text style={styles.textRightBold}>세액: {vat.toLocaleString()} 원</Text></View>
          </View>
          <View style={{ flexDirection: 'row', backgroundColor: '#e2e8f0' }}>
            <View style={[styles.td, { width: '100%', paddingVertical: 12 }]}><Text style={{ width: '100%', textAlign: 'center', color: '#000000', fontSize: 13, fontWeight: 'bold' }}>총 청구 금액 (VAT포함): {total.toLocaleString()} 원</Text></View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={{ width: '100%', textAlign: 'center', fontSize: 10 }}>위와 같이 계산(영수) 합니다.</Text>
          <View style={styles.signBox}>
            <Text style={{ fontSize: 14, fontWeight: 'bold' }}>주식회사 이투빌리티 (인)</Text>
            <View style={{ width: 40, height: 40, borderRadius: 20, borderWidth: 1, borderColor: '#ef4444', justifyContent: 'center', alignItems: 'center' }}>
              <Text style={{ width: '100%', textAlign: 'center', fontSize: 11, color: '#ef4444', fontWeight: 'bold' }}>직인</Text>
            </View>
          </View>
          <Text style={{ width: '100%', textAlign: 'center', marginTop: 20, fontSize: 10, fontWeight: 'bold', color: '#000000' }}>IBK기업은행 377-139129-04-011 예금주: (주) 이투빌리티</Text>
        </View>

      </Page>
    </Document>
  );
};

export default PartSalesInvoicePdf;